import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Layers, Plus, Globe, Loader2, ArrowUpRight } from 'lucide-react'
import { getSession } from '../lib/session'
import { byKey } from '../lib/serviceCatalog'
import ConnectorGrid from '../components/ConnectorGrid'
import KnowledgeView from '../components/KnowledgeView'

// Where each service lives, so a service chip on an instance is a live link.
const SERVICE_PATH = {
  answers: '/answers', sync: '/sync', rag: '/rag', orchestration: '/workflows',
  middleware: '/adapters', intake: '/intake', agent: '/setup',
}
const hostOf = (u) => { try { return new URL(u).hostname.replace(/^www\./, '') } catch { return null } }

export default function Instances() {
  const session = getSession()
  const ns = session?.namespaceId
  const [instances, setInstances] = useState(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ name: '', sourceUrl: '' })
  const [busy, setBusy] = useState(false)
  const [openSource, setOpenSource] = useState(null)
  const [knowledgeKey, setKnowledgeKey] = useState(0)

  const load = () => {
    if (!ns) return
    axios.get(`/api/v1/tenants/${ns}/instances`).then(r => setInstances(r.data.instances || [])).catch(() => setInstances([]))
  }
  useEffect(load, [ns])

  const create = async () => {
    setBusy(true)
    try {
      await axios.post(`/api/v1/tenants/${ns}/instances`, { name: form.name.trim() || undefined, sourceUrl: form.sourceUrl.trim() || undefined })
      toast.success('Instance created'); setForm({ name: '', sourceUrl: '' }); setCreating(false); load()
    } catch (err) { toast.error(err.response?.data?.error || 'Could not create') } finally { setBusy(false) }
  }

  if (!ns) return (
    <div className="card bg-amber-500/10 border-amber-500/30 text-sm text-amber-600 max-w-lg">
      No workspace connected. Go to <Link to="/setup" className="font-semibold underline">Workspace</Link> first.
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Layers size={22} /> Instances</h2>
          <p className="text-gray-500 mt-1 max-w-2xl">An instance is <b>one data source</b>. Every service you turn on for it — chatbot, sync, RAG, intake, adapters, workflows, agent — shares that instance’s knowledge. Spin up a new instance only for a genuinely different source.</p>
        </div>
        <button onClick={() => setCreating(v => !v)} className="btn-primary flex items-center gap-2"><Plus size={16} /> New instance</button>
      </div>

      {creating && (
        <div className="card max-w-xl space-y-3">
          <h3 className="font-semibold text-gray-900">New data source</h3>
          <input className="input" placeholder="Name (e.g. Support site, Internal HR)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input className="input" placeholder="https://yourcompany.com (optional — connect any source after)" value={form.sourceUrl} onChange={e => setForm(f => ({ ...f, sourceUrl: e.target.value }))} />
          <div className="flex gap-2">
            <button onClick={create} disabled={busy} className="btn-primary disabled:opacity-60 flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create</button>
            <button onClick={() => setCreating(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {instances === null ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={18} /> Loading…</div>
      ) : (
        <div className="space-y-4">
          {instances.map((inst) => {
            const title = hostOf(inst.sourceUrl) || inst.name
            return (
              <div key={inst.id} className="card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {title}
                      {inst.isDefault && <span className="text-[10px] uppercase tracking-wide bg-primary-500/10 text-primary-600 px-1.5 py-0.5 rounded">primary source</span>}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
                      <Globe size={13} /> {inst.sourceUrl || 'Standalone — fed from uploads / database / etc.'}
                    </p>
                  </div>
                  <button onClick={() => setOpenSource(openSource === inst.id ? null : inst.id)} className="text-sm font-medium text-primary-600 hover:underline whitespace-nowrap">
                    {openSource === inst.id ? 'Close' : '+ Add a source'}
                  </button>
                </div>

                {/* services on this instance — each a live link to its page, with a status dot */}
                <div className="mt-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Services on this source</p>
                  {inst.services.length === 0 ? (
                    <span className="text-xs text-gray-400">No services here yet — activate one from <Link to="/" className="text-primary-600 hover:underline">Services</Link>.</span>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {inst.services.map((s) => {
                        const svc = byKey(s)
                        const to = SERVICE_PATH[s] || '/'
                        return (
                          <Link key={s} to={to}
                            className="inline-flex items-center gap-1.5 text-sm bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm rounded-lg px-2.5 py-1.5 transition-all">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Active" />
                            {svc?.name || s}
                            <ArrowUpRight size={13} className="text-gray-400" />
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* connected sources */}
                {Array.isArray(inst.connectedSources) && inst.connectedSources.length > 0 && (
                  <p className="mt-3 text-xs text-gray-500">Connected: {inst.connectedSources.map((c, i) => <span key={i} className="mr-2">{c.label || c.type}</span>)}</p>
                )}

                {openSource === inst.id && (
                  <div className="mt-4 border-t border-gray-100 pt-4">
                    <p className="text-sm text-gray-600 mb-3">Connect a source to <b>{title}</b> — click one; every service on this instance will use it.</p>
                    <ConnectorGrid dataInstanceId={inst.id} onDone={() => { load(); setKnowledgeKey(k => k + 1) }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Your data — what's been indexed across this workspace */}
      {instances && instances.length > 0 && <KnowledgeView key={knowledgeKey} />}
    </div>
  )
}
